import { getCalendarInsuranceSelectedForAvailability } from '@components/InsurancesForm/CalendarInsuranceForm/CalendarInsuranceForm.utils';
import type { InsuranceInfo, Patient } from '@store/patientStore';
import type { SlotFromRescheduleFlow } from './CalendarPage.interface';
import type { HourSlot, Slot } from '@utils/interfaces';
import dayjs from 'dayjs';

// Crea sempre i tre gruppi AM/PM/evening e copia gli slot BFF senza mutare la risposta originale.
export const createHourSlotGroups = (slots?: Slot[]): Slot[] => {
  const tempSlots: Slot[] = [
    {
      list: [],
      timeSlotOrder: 1,
    },
    {
      list: [],
      timeSlotOrder: 2,
    },
    {
      list: [],
      timeSlotOrder: 3,
    },
  ];

  for (const slot of slots || []) {
    tempSlots[slot.timeSlotOrder - 1].list = slot.list.map(hourSlot => ({
      ...hourSlot,
      internalSlots: (hourSlot.internalSlots || []).map(internalSlot => ({
        ...internalSlot,
      })),
    }));
  }

  return tempSlots;
};

// Restituisce l'identificativo stabile dello slot, usando gli internal slot quando manca slotId.
export const getHourSlotId = (slot?: HourSlot) =>
  slot?.slotId ||
  `${slot?.internalSlots?.[0]?.slotId} - ${slot?.internalSlots?.[0]?.doctorId || ''}`;

type CalendarTranslate = (key: string) => string;

export const getHourSlotSelectionLabel = ({
  slot,
  isCurrent,
  isPreviousSlot,
  inRescheduleFlow,
  isError,
  showYourSelection,
  t,
}: {
  slot: HourSlot;
  isCurrent?: boolean;
  isPreviousSlot: boolean;
  inRescheduleFlow: boolean;
  isError: boolean;
  showYourSelection: boolean;
  t: CalendarTranslate;
}): string | undefined => {
  if (isPreviousSlot) {
    return t('calendarPage.previousSelection');
  }

  if (inRescheduleFlow && isCurrent) {
    return t('calendarPage.newSelection');
  }

  if (slot.patientName) {
    return slot.patientName;
  }

  if (isError) {
    return undefined;
  }

  if (isCurrent && showYourSelection) {
    return t('appointmentTypePage.yourSelection');
  }

  return '';
};

// Confronta due slot usando prima l'identita stabile e poi i dati principali dello slot.
export const isSameHourSlot = (first?: HourSlot, second?: HourSlot) => {
  if (!first || !second) return false;

  const firstSlotId = getHourSlotId(first);
  const secondSlotId = getHourSlotId(second);

  if (firstSlotId && secondSlotId && firstSlotId === secondSlotId) {
    return true;
  }

  return (
    first.startTime === second.startTime &&
    first.doctorId === second.doctorId &&
    first.doctorName === second.doctorName
  );
};

// Sposta lo slot selezionato nel tab orario corretto rimuovendo eventuali duplicati dagli altri tab.
export const addSelectedSlotToTimeTab = (
  tempSlots: Slot[],
  selectedSlot: HourSlot,
  timeTabIndex: number,
) => {
  if (!tempSlots[timeTabIndex]) return;

  tempSlots.forEach(slotGroup => {
    slotGroup.list = slotGroup.list.filter(
      slot => !isSameHourSlot(slot, selectedSlot),
    );
  });

  tempSlots[timeTabIndex].list.push(selectedSlot);
};

// Verifica se e' cambiato il valore insurance che guida le availability del calendario.
export const hasCalendarInsuranceAvailabilityChanged = (
  currentInsurance?: InsuranceInfo,
  nextInsurance?: InsuranceInfo,
) =>
  getCalendarInsuranceSelectedForAvailability(currentInsurance) !==
  getCalendarInsuranceSelectedForAvailability(nextInsurance);

export const shouldShowScheduleInsurance = ({
  askForInsuranceInformation,
  insuranceMustDriveAvailability,
  inRescheduleFlow,
}: {
  askForInsuranceInformation?: boolean;
  insuranceMustDriveAvailability?: boolean;
  inRescheduleFlow?: boolean;
}) =>
  !inRescheduleFlow &&
  !!askForInsuranceInformation &&
  insuranceMustDriveAvailability === true;

// Ricava l'indice del time tab partendo dall'orario di inizio dello slot.
export const getTimeTabIndexBySlotStartTime = (slot: HourSlot) => {
  const slotStart = dayjs(slot.startTime);
  const slotMinutes = slotStart.hour() * 60 + slotStart.minute();

  if (slotMinutes >= 8 * 60 && slotMinutes <= 11 * 60 + 59) return 0;
  if (slotMinutes >= 12 * 60 && slotMinutes <= 15 * 60 + 59) return 1;
  if (slotMinutes >= 16 * 60 && slotMinutes <= 20 * 60) return 2;

  return -1;
};

// Converte giorno BFF e ora AM/PM in una stringa ISO-like stabile per tutti i browser.
export const buildISOLikeDate = (day: string, hour: string) => {
  const [time, mer] = hour.split(' ');
  const [h, m] = time.split(':').map(Number);
  const hours = (h % 12) + (mer?.toUpperCase() === 'PM' ? 12 : 0);
  const hh = hours.toString().padStart(2, '0');
  const mm = m.toString().padStart(2, '0');

  return {
    isoLikeDate: `${day}T${hh}:${mm}:00`,
    time24: `${hh}:${mm}`,
  };
};

// Inizializza lo stato di completamento Schedule Exam per il multiple booking.
export const buildInitialListEnableButton = (
  patients: Array<Patient | undefined>,
  currentPatientIndex: number,
): Array<boolean | undefined> =>
  patients.map((patient, index) => {
    if (!patient) return undefined;

    if (index === currentPatientIndex) {
      return false;
    }

    return (
      (!!patient.reservationId || !!patient.appointmentId) &&
      !patient.appointmentType.isModified &&
      !patient.insurance?.isModified
    );
  });

// Costruisce i nomi dei pazienti che devono ancora ricontrollare lo slot prima del Continue.
export const buildPatientsToCheck = (
  listEnableButton: Array<boolean | undefined>,
  currentPatientIndex: number,
  patients: Array<Patient | undefined>,
): string[] =>
  listEnableButton.reduce<string[]>((acc, isEnabled, index) => {
    if (
      isEnabled !== undefined &&
      !isEnabled &&
      index !== currentPatientIndex &&
      patients[index]
    ) {
      const patient = patients[index]?.patientDetails;
      acc.push(
        `${patient?.firstName ?? 'New'} ${patient?.lastName ?? 'Patient'}`,
      );
    }

    return acc;
  }, []);

// Decide quale tipo di chooseNewSlot mostrare in base alle modifiche appointment/insurance.
export const getChooseNewSlotType = <T>({
  appointment,
  appointmentTypeModified,
  both,
  insurance,
  insuranceModified,
}: {
  appointment: T;
  appointmentTypeModified?: boolean;
  both: T;
  insurance: T;
  insuranceModified?: boolean;
}): T => {
  if (appointmentTypeModified && insuranceModified) {
    return both;
  }

  if (appointmentTypeModified) {
    return appointment;
  }

  return insurance;
};

// Ordina gli hour slot in modo deterministico per data, doctorId e startTime.
export const sortHourSlots = (hourSlots: HourSlot[]): HourSlot[] => {
  return hourSlots.sort((a, b) => {
    const byDay = dayjs(a?.startTime).valueOf() - dayjs(b?.startTime).valueOf();
    if (byDay !== 0) return byDay;
    const byDoctorId = a?.doctorId?.localeCompare(b?.doctorId ?? '') ?? 0;
    if (byDoctorId !== 0) return byDoctorId;
    return a?.startTime?.localeCompare(b?.startTime ?? '') ?? 0;
  });
};

export const isEnableListComplete = (
  listEnableButton: Array<boolean | undefined>,
) =>
  listEnableButton.every(
    enable => (typeof enable === 'boolean' && enable) || enable === undefined,
  );

export const isScheduleExamStepComplete = ({
  insuranceEnableButtonList,
  isInsurancesLoading,
  isLoadingSlot,
  listEnableButton,
  showInsuranceInSchedule,
}: {
  insuranceEnableButtonList: Array<boolean | undefined>;
  isInsurancesLoading: boolean;
  isLoadingSlot: boolean;
  listEnableButton: Array<boolean | undefined>;
  showInsuranceInSchedule: boolean;
}) =>
  isEnableListComplete(listEnableButton) &&
  (!showInsuranceInSchedule ||
    isEnableListComplete(insuranceEnableButtonList)) &&
  !isLoadingSlot &&
  (!showInsuranceInSchedule || !isInsurancesLoading);

export const getSchedulePatientDisplayName = ({
  fallbackPatientName,
  patient,
}: {
  fallbackPatientName: string;
  patient?: Patient;
}) => {
  const firstName = patient?.patientDetails?.firstName?.trim();
  const lastName = patient?.patientDetails?.lastName?.trim();

  return firstName || lastName
    ? [firstName, lastName].filter(Boolean).join(' ')
    : fallbackPatientName;
};

export const getIncompleteScheduleExamPatientNames = ({
  currentPatientIndex,
  fallbackPatientName,
  insuranceEnableButtonList,
  listEnableButton,
  patients,
  showInsuranceInSchedule,
}: {
  currentPatientIndex: number;
  fallbackPatientName: string;
  insuranceEnableButtonList: Array<boolean | undefined>;
  listEnableButton: Array<boolean | undefined>;
  patients: Array<Patient | undefined>;
  showInsuranceInSchedule: boolean;
}) =>
  patients.reduce<string[]>((acc, patient, index) => {
    const scheduleIncomplete = listEnableButton[index] === false;
    const insuranceIncomplete =
      showInsuranceInSchedule && insuranceEnableButtonList[index] === false;

    if (
      patient &&
      index !== currentPatientIndex &&
      (scheduleIncomplete || insuranceIncomplete)
    ) {
      acc.push(getSchedulePatientDisplayName({ fallbackPatientName, patient }));
    }

    return acc;
  }, []);

export const isPreviousSlotFromRescheduleFlow = ({
  previousSlotToDisable,
  slot,
}: {
  previousSlotToDisable?: SlotFromRescheduleFlow;
  slot: HourSlot;
}) => {
  if (!previousSlotToDisable) {
    return false;
  }

  const { doctor, day, hour } = previousSlotToDisable;
  const { isoLikeDate: previousSlotDate } = buildISOLikeDate(day, hour);

  return slot.startTime === previousSlotDate && slot.doctorName === doctor;
};

export const getTimeTabOrderForSelectedSlot = (
  slots: Slot[] | undefined,
  selectedSlot: HourSlot | undefined,
) => {
  if (!slots || !selectedSlot) {
    return undefined;
  }

  return slots.find(slotGroup =>
    slotGroup.list.some(slot => isSameHourSlot(slot, selectedSlot)),
  )?.timeSlotOrder;
};

export const shouldShowRequiredSlotError = ({
  getSlotsIsLoading,
  hasAvailableSlotToSelect,
  isLoadingSlot,
  selectedHourSlot,
  submitAttemptCount,
}: {
  getSlotsIsLoading: boolean;
  hasAvailableSlotToSelect: boolean;
  isLoadingSlot: boolean;
  selectedHourSlot?: HourSlot;
  submitAttemptCount: number;
}) =>
  submitAttemptCount > 0 &&
  !selectedHourSlot &&
  !isLoadingSlot &&
  !getSlotsIsLoading &&
  hasAvailableSlotToSelect;
